/**
 * GlobalView — EONET Natural Events Layer Renderer
 *
 * Renders NASA EONET natural events (volcanoes, storms, floods,
 * landslides, ice, etc.) as category-colored, pulsing point markers
 * on the globe with event-title labels.
 *
 * Architecture:
 *   - PointGraphics per event (clamped to ground)
 *   - Color mapped from EONET category
 *   - Subtle pulsing animation via CallbackProperty
 *   - Label with event title (visible at medium zoom)
 *   - 30-minute refresh interval (EONET updates infrequently)
 *   - requestAnimationFrame drives continuous rendering
 *
 * Data: NASA EONET v3 GeoJSON (no API key needed)
 */

import * as Cesium from 'cesium';
import { useGlobeStore } from '../../stores/globeStore';
import { useEntityStore, selectNaturalEventCount } from '../../stores/entityStore';
import {
  fetchNaturalEvents,
  categoryToColor,
  categoryToIcon,
} from '../eonetClient';
import type { Entity } from '@globalview/shared';

// ─── Config ──────────────────────────────────────────────────────

/** Max events to render */
const MAX_EVENTS = 200;

/** Data refresh interval (ms) — 30 minutes */
const REFRESH_INTERVAL_MS = 30 * 60 * 1000;

/** Pulse animation cycle (ms) */
const PULSE_CYCLE_MS = 3000;

/** Base pixel size for event dots */
const BASE_PIXEL_SIZE = 10;

/** Pulse amplitude (fraction of base size) */
const PULSE_AMPLITUDE = 0.25;

/** Base alpha */
const BASE_ALPHA = 0.85;

/** Alpha pulse range */
const ALPHA_PULSE = 0.12;

// ─── Module State ────────────────────────────────────────────────

interface EonetLayerState {
  viewer: Cesium.Viewer;
  dataSource: Cesium.CustomDataSource;
  unsubscribeVisibility: (() => void) | null;
  refreshTimer: ReturnType<typeof setInterval> | null;
  loading: boolean;
  loaded: boolean;
}

let state: EonetLayerState | null = null;
let animFrameId: number | null = null;
let animRunning = false;

// ─── Hash → Phase Offset ─────────────────────────────────────────

function hashToPhase(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return (Math.abs(hash) % 1000) / 1000;
}

// ─── Entity Creation ─────────────────────────────────────────────

function buildEntities(events: Entity[]): void {
  if (!state) return;

  const { dataSource } = state;
  dataSource.entities.removeAll();

  const sorted = [...events].slice(0, MAX_EVENTS);

  for (const evt of sorted) {
    const props = evt.properties as Record<string, unknown>;
    const categoryId = (props.categoryId as string) ?? '';
    const title = (props.title as string) ?? 'Unknown Event';
    const icon = categoryToIcon(categoryId);
    const baseColor = Cesium.Color.fromCssColorString(categoryToColor(categoryId));
    const phaseOffset = hashToPhase(evt.id);

    const pointColor = baseColor.clone();

    // ── Point at ground level ──
    dataSource.entities.add({
      id: evt.id,
      position: Cesium.Cartesian3.fromDegrees(
        evt.position.lon,
        evt.position.lat,
        500,
      ),
      point: {
        pixelSize: new Cesium.CallbackProperty(() => {
          const t = ((Date.now() / PULSE_CYCLE_MS) + phaseOffset) % 1.0;
          const pulse = Math.sin(t * Math.PI * 2) * 0.5 + 0.5;
          return BASE_PIXEL_SIZE * (1 + PULSE_AMPLITUDE * pulse);
        }, false) as unknown as Cesium.Property,
        color: new Cesium.CallbackProperty(() => {
          const t2 = ((Date.now() / PULSE_CYCLE_MS) + phaseOffset) % 1.0;
          const pulse2 = Math.sin(t2 * Math.PI * 2) * 0.5 + 0.5;
          pointColor.alpha = BASE_ALPHA + ALPHA_PULSE * pulse2;
          return pointColor;
        }, false) as unknown as Cesium.Property,
        outlineColor: baseColor.withAlpha(0.4),
        outlineWidth: 2,
        heightReference: new Cesium.ConstantProperty(
          Cesium.HeightReference.CLAMP_TO_GROUND,
        ),
        disableDepthTestDistance: 5000,
        scaleByDistance: new Cesium.NearFarScalar(1e4, 1.5, 1e7, 0.5),
        distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, 30_000_000),
      },
      label: {
        text: `${icon} ${title}`,
        font: '12px Inter, Helvetica, sans-serif',
        fillColor: Cesium.Color.WHITE,
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 3,
        style: Cesium.LabelStyle.FILL_AND_OUTLINE,
        pixelOffset: new Cesium.Cartesian2(0, -18),
        scaleByDistance: new Cesium.NearFarScalar(1e4, 1.0, 5e6, 0.0),
        distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, 5_000_000),
      },
      properties: new Cesium.PropertyBag({
        entityId: evt.id,
        title: props.title,
        description: props.description,
        eonetId: props.eonetId,
        link: props.link,
        closed: props.closed,
        magnitudeValue: props.magnitudeValue,
        magnitudeUnit: props.magnitudeUnit,
        categoryId: props.categoryId,
        categoryTitle: props.categoryTitle,
        sourceId: props.sourceId,
        sourceUrl: props.sourceUrl,
        date: evt.timestamp,
      }),
    });
  }

  console.log(
    `[EonetLayer] Built ${sorted.length} natural event entities ` +
    `(${dataSource.entities.values.length} total)`,
  );
}

// ─── Animation ───────────────────────────────────────────────────

function animLoop(): void {
  if (!state || !animRunning) return;
  state.viewer.scene.requestRender();
  animFrameId = requestAnimationFrame(animLoop);
}

function startAnimation(): void {
  if (animRunning) return;
  animRunning = true;
  animFrameId = requestAnimationFrame(animLoop);
  console.log('[EonetLayer] Animation started');
}

function stopAnimation(): void {
  animRunning = false;
  if (animFrameId != null) {
    cancelAnimationFrame(animFrameId);
    animFrameId = null;
  }
}

// ─── Data Loading ────────────────────────────────────────────────

async function loadData(): Promise<void> {
  if (!state || state.loading) return;

  state.loading = true;
  console.log('[EonetLayer] Loading EONET data...');

  try {
    const entities = await fetchNaturalEvents();

    if (entities.length === 0) {
      console.warn('[EonetLayer] No event data received');
      return;
    }

    useEntityStore.getState().clearByType('natural_event');
    useEntityStore.getState().upsertEntities(entities);

    buildEntities(entities);
    startAnimation();

    state.loaded = true;
    console.log(`[EonetLayer] Loaded ${entities.length} natural events`);
  } catch (err) {
    console.error('[EonetLayer] Failed to load data:', err);
  } finally {
    if (state) state.loading = false;
  }
}

// ─── Public API ──────────────────────────────────────────────────

export function initEonetLayer(viewer: Cesium.Viewer): void {
  if (state) return;

  const dataSource = new Cesium.CustomDataSource('natural-events');
  viewer.dataSources.add(dataSource);

  state = {
    viewer,
    dataSource,
    unsubscribeVisibility: null,
    refreshTimer: null,
    loading: false,
    loaded: false,
  };

  const visible = useGlobeStore.getState().eonetLayerVisible;
  dataSource.show = visible;

  state.unsubscribeVisibility = useGlobeStore.subscribe((curr, prev) => {
    if (!state) return;

    if (curr.eonetLayerVisible !== prev.eonetLayerVisible) {
      state.dataSource.show = curr.eonetLayerVisible;

      if (curr.eonetLayerVisible) {
        if (!state.loaded) {
          loadData();
        } else {
          startAnimation();
        }
        if (!state.refreshTimer) {
          state.refreshTimer = setInterval(() => loadData(), REFRESH_INTERVAL_MS);
        }
      } else {
        stopAnimation();
        if (state.refreshTimer) {
          clearInterval(state.refreshTimer);
          state.refreshTimer = null;
        }
      }

      viewer.scene.requestRender();
    }
  });

  if (visible) {
    loadData();
    state.refreshTimer = setInterval(() => loadData(), REFRESH_INTERVAL_MS);
  }

  console.log('[EonetLayer] Initialized');
}

export function destroyEonetLayer(): void {
  if (!state) return;

  if (state.unsubscribeVisibility) state.unsubscribeVisibility();
  if (state.refreshTimer) clearInterval(state.refreshTimer);
  stopAnimation();

  state.viewer.dataSources.remove(state.dataSource, true);
  useEntityStore.getState().clearByType('natural_event');

  state = null;
  console.log('[EonetLayer] Destroyed');
}

export function getNaturalEventCount(): number {
  return selectNaturalEventCount(useEntityStore.getState());
}
